import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const specialChars = /[!#$%@&~]/;
const numberChars = /[0-9]/;
const lowerChars = /[a-z]/;
const upperChars = /[A-Z]/;
const commonPasswords = [
  'password', '12345678', 'qwerty', 'abc123', '11111111', 'iloveyou', 'admin', 'welcome'
];

function isValidPassword(password, personalInfo = '') {
  if (password.length < 8) return "Password must contain at least 8 characters.";
  if (commonPasswords.includes(password.toLowerCase())) return "Password can't be a commonly used password.";
  if (/^\d+$/.test(password)) return "Password can't be entirely numeric.";
  if (password.startsWith(' ') || password.endsWith(' ')) return "Password must not start or end with a space.";
  if (!numberChars.test(password)) return "Password must contain at least one number.";
  if (!specialChars.test(password)) return "Password must contain at least one special character (! # $ % @ & ~).";
  if (!lowerChars.test(password) || !upperChars.test(password)) return "Password must contain at least one lowercase and one uppercase character.";
  if (personalInfo && password.toLowerCase().includes(personalInfo.toLowerCase())) return "Password can't be too similar to your other personal information.";
  return "";
}

const ResetPassword = ({ personalInfo = '' }) => {
  const [email, setEmail] = useState('');
  const location = useLocation();
  // Autofill email from URL if present
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlEmail = params.get('email');
    if (urlEmail) setEmail(urlEmail);
  }, [location.search]);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setSuccess('');
    const validationMsg = isValidPassword(password, personalInfo);
    if (validationMsg) {
      setError(validationMsg);
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    try {
  const res = await fetch(`${process.env.REACT_APP_SERVICE_DOMAIN}/resetpassword`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess(data.message || "Password has been reset successfully.");
        navigate('/passwordconfirmation');
      } else {
        setError(data.message || "Error resetting password.");
      }
    } catch {
      setError("Error resetting password.");
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h2 style={styles.title}>Reset Password</h2>
        <form onSubmit={handleSubmit}>
          <input
            type="email"
            placeholder="Email address"
            value={email}
            onChange={e => setEmail(e.target.value)}
            style={styles.input}
            required
            disabled={Boolean(email)}
          />
          <input
            type="password"
            placeholder="New password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            style={styles.input}
            required
          />
          <input
            type="password"
            placeholder="Confirm new password"
            value={confirm}
            onChange={e => setConfirm(e.target.value)}
            style={styles.input}
            required
          />
          <button type="submit" style={styles.button}>Change my Password</button>
        </form>
        <ul style={styles.criteria}>
          <li>Your password must contain at least 8 characters.</li>
          <li>Your password can’t be a commonly used password.</li>
          <li>Your password can’t be entirely numeric.</li>
          <li>Your password must not start or end with a space.</li>
          <li>Your password must contain at least one number.</li>
          <li>Your password must contain at least one special character (! # $ % @ & ~).</li>
          <li>Your password must contain at least one lowercase and one uppercase character.</li>
          <li>Your password can’t be too similar to your other personal information.</li>
        </ul>
        {error && <div style={styles.error}>{error}</div>}
        {success && <div style={styles.success}>{success}</div>}
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    background: '#f5f6fa',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  card: {
    background: '#fff',
    padding: '40px',
    borderRadius: '12px',
    boxShadow: '0 2px 16px rgba(0,0,0,0.12)',
    maxWidth: '400px',
    width: '100%',
    textAlign: 'center',
  },
  title: {
    color: '#4078c0',
    fontSize: '2rem',
    marginBottom: '18px',
  },
  input: {
    width: '100%',
    padding: '12px',
    borderRadius: '6px',
    border: '1px solid #dbe6f6',
    marginBottom: '18px',
    fontSize: '1rem',
  },
  button: {
    width: '100%',
    padding: '12px',
    borderRadius: '6px',
    background: '#4078c0',
    color: '#fff',
    fontWeight: 'bold',
    fontSize: '1rem',
    border: 'none',
    cursor: 'pointer',
    marginBottom: '18px',
  },
  criteria: {
    textAlign: 'left',
    fontSize: '0.95rem',
    color: '#555',
    marginBottom: '18px',
    paddingLeft: '18px',
  },
  error: {
    color: '#d32f2f',
    marginTop: '12px',
    fontWeight: 'bold',
  },
  success: {
    color: '#388e3c',
    marginTop: '12px',
    fontWeight: 'bold',
  },
};

export default ResetPassword